import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { HeaderRightSlotProvider } from '@/contexts/HeaderRightSlotContext';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { Theme1BackgroundOverlay } from '@/components/Theme1BackgroundOverlay';
import AuthenticatedHeader from './components/Header/AuthenticatedHeader';
import AuthenticatedFooter from './components/Footer/AuthenticatedFooter';
import MobilePageTitle from './components/MobilePageTitle';
import GamifyWidgetMount from '@/components/GamifyWidgetMount';
import KioskInAppPopupGate from '@/components/KioskInAppPopupGate';
import { PostLoginTourGate } from '@/components/TourGuide';

/** Routes that use fixed 80px header/body/80px footer layout (no full-page scroll). */
const FIXED_CHROME_PATHS: string[] = [
  '/account',
  '/account/finance/deposit',
  '/account/finance/payment-checkout',
  '/account/finance/payout-request',
  '/account/finance/wallet',
  '/account/finance/withdrawals',
  '/account/games',
  '/account/settings',
  '/account/transactions',
  '/account/wallet/deposit-checkout',
  '/account/wallet/payout-request',
];

function useFixedChromeLayout(pathname: string): boolean {
  return FIXED_CHROME_PATHS.some((path) => pathname === path);
}

interface AuthenticatedLayoutProps {
  header?: React.ReactNode | 'default' | 'none';
  footer?: React.ReactNode | 'default' | 'none';
  bodyClassName?: string;
}

/**
 * Layout component for authenticated pages
 * Structure: Header + Body (mobile page title + Outlet) + Footer
 * On FIXED_CHROME_PATHS: fixed 80px header, flex body (scrollable), 80px footer; responsive.
 */
const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  header = 'default',
  footer = 'default',
  bodyClassName = '',
}) => {
  const fullScreenBg = usePageFullScreenBackground();
  const location = useLocation();
  const useFixedChrome = useFixedChromeLayout(location.pathname);

  const headerNode = header === 'none' ? null : header === 'default' ? (
    <AuthenticatedHeader compact={useFixedChrome} />
  ) : (
    header
  );

  const footerNode = footer === 'none' ? null : footer === 'default' ? (
    <AuthenticatedFooter compact={useFixedChrome} />
  ) : (
    footer
  );

  if (useFixedChrome) {
    return (
      <HeaderRightSlotProvider>
        <GamifyWidgetMount />
        <KioskInAppPopupGate />
        <PostLoginTourGate />
        <div
          {...fullScreenBg}
          className={`safe-area-top safe-area-bottom ${fullScreenBg.className} flex flex-col h-screen overflow-hidden`}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Theme1BackgroundOverlay />
          {/* Header: fixed 80px */}
          {headerNode != null && (
            <div
              className="relative z-30 h-[80px] flex-shrink-0 min-h-0 overflow-visible"
              role="banner"
            >
              {headerNode}
            </div>
          )}

          {/* Body: fills remaining space, scrollable */}
          <main className={`flex-1 min-h-0 flex flex-col overflow-hidden ${bodyClassName}`} style={{ position: 'relative', zIndex: 1 }}>
            <MobilePageTitle />
            <div className="flex-1 min-h-0 overflow-auto overflow-x-hidden">
              <Outlet />
            </div>
          </main>

          {/* Footer: fixed 80px */}
          {footerNode != null && (
            <div className="h-[80px] flex-shrink-0 overflow-hidden">
              {footerNode}
            </div>
          )}
        </div>
      </HeaderRightSlotProvider>
    );
  }

  return (
    <HeaderRightSlotProvider>
      <GamifyWidgetMount />
      <KioskInAppPopupGate />
      <PostLoginTourGate />
      <div
        {...fullScreenBg}
        className={`safe-area-top safe-area-bottom ${fullScreenBg.className} flex flex-col overflow-x-hidden overflow-y-auto`}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Theme1BackgroundOverlay />
        {headerNode}
        <main className={`flex-1 min-w-0 flex flex-col ${bodyClassName}`} style={{ position: 'relative', zIndex: 1 }}>
          <MobilePageTitle />
          <Outlet />
        </main>
        {footerNode}
      </div>
    </HeaderRightSlotProvider>
  );
};

export default AuthenticatedLayout;
